import _ from "lodash";

import {
  APP_CODE,
  AppConfigProvider,
  AppConfigService,
  HttpService,
  UrlType,
} from "@sportal/api";
import { Durations } from "@sportal/api/reports";
import { UNITS } from "../components/pauseInternet/pauseInternet.helpers";
import { AppConfig, InternetPause, UrlFilters } from "./appConfigService.types";

export const defaultLanguage = {
  key: "us_EG",
  name: "English",
  inversedDirection: false,
};

const defaults: Partial<AppConfig> = {
  languages: {
    default: "us_EG",
    list: [defaultLanguage],
  },
  footerCompanyLink: "https://akamai.com",
  logoLink: "/home",
  helpPageLink: "#/help",
  termsLink: "",
  security: {
    enabled: true,
    noLoginPage: false,
    goBackOnLoginErrorLink: "",
    goBackOnLoginErrorLinkText: "",
    logoutUrl: "#/login",
  },
  defaultReportGranularity: Durations.Day,
  showCountsForReports: false,
  showPerProfileReporting: false,
  appTitle: "Secure Internet Access Portal",
  urlFilters: {
    "url-types": [UrlType.CORE_DOMAIN],
  },
  internetPauseOptions: null,
  showToggles: [],
  firstDayOfWeek: "MO",
  hideDevicesTab: false,
  reportingTimeoutMinutes: 0,
};

export const mergeLink = (
  defaultValue: string,
  configValue: string
): string => {
  const validateLink = (link: string) => /^https?:\/\//i.test(link);
  const isLinkValid = validateLink(configValue);

  return isLinkValid ? configValue : defaultValue;
};

const mergeString = (defaultValue: string, configValue: string): string =>
  configValue || defaultValue;

const mergeReportGranularity = (
  defaultValue: Durations,
  configValue: Durations | string
): Durations =>
  _.includes(Durations, configValue)
    ? (configValue as Durations)
    : defaultValue;

export const filterInvalidOptions = (
  defaultOptions: InternetPause[],
  configOptions: InternetPause[]
): InternetPause[] => {
  const filteredPauseOptions = _.chain(configOptions)
    .map(({ amount, units }) => ({ amount: _.floor(amount), units }))
    .filter((option) => option.amount >= 1 && _.has(UNITS, option.units))
    .uniqWith(_.isEqual)
    .value();

  return _.isEmpty(filteredPauseOptions)
    ? defaultOptions
    : filteredPauseOptions;
};

const allowedUrlFilters = (
  defaultOptions: UrlFilters,
  configOptions: UrlFilters
): UrlFilters => {
  const allowedTypes = [
    UrlType.CORE_DOMAIN,
    UrlType.FQDN,
    UrlType.PUBLIC_SUFFIX,
  ];
  const config = configOptions || defaultOptions;

  return {
    ...config,
    "url-types": config["url-types"].filter((url) =>
      allowedTypes.includes(url)
    ),
  };
};

const customizers = {
  footerCompanyLink: mergeLink,
  logoLink: mergeLink,
  appTitle: mergeString,
  termsLink: mergeLink,
  logoutUrl: mergeLink,
  defaultReportGranularity: mergeReportGranularity,
  internetPauseOptions: filterInvalidOptions,
  urlFilters: allowedUrlFilters,
};

const isDefaultAppConfigValue = (path: string, value: string): boolean =>
  _.get(defaults, path) === value;

export const isLogoutUrlDefault = (value: string): boolean =>
  isDefaultAppConfigValue("security.logoutUrl", value);

export const SC_CODE = APP_CODE.PI;

export const makeAppConfigService = (
  http: HttpService
): AppConfigService<AppConfig> =>
  new AppConfigService(defaults, customizers, new AppConfigProvider(http));
